import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  CopyPlus,
  ImageOff,
  Link2,
  Pencil,
  Power,
  Trash2,
} from 'lucide-react';
import type { Product } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { api, extractError } from '../../lib/api';
import { formatEur } from '../../lib/format';
import { seoDefaults } from '../../seo/seo.config';
import { useToast } from '../../components/admin/Toast';
import { ConfirmDialog } from '../../components/admin/ConfirmDialog';
import { ActionMenu } from '../../components/admin/ActionMenu';

interface ProductListResponse {
  items: Product[];
  total: number;
  totalPages: number;
  page: number;
}

const inputClass =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const secondaryBtn =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm hover:bg-admin-surface-alt disabled:opacity-50';
const thClass =
  'p-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-muted';
const thRight = `${thClass} text-right`;
const actionBtn =
  'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm transition-colors';
const editBtn = `${actionBtn} border-admin-border bg-white text-admin-text hover:bg-admin-surface-alt`;

export default function AdminProductsPage(): JSX.Element {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pendingDelete, setPendingDelete] = useState<Product | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(sp.get('search') ?? '');
  const [refreshKey, setRefreshKey] = useState(0);
  const searchQuery = sp.get('search') ?? '';
  const statusFilter = sp.get('status') ?? 'all';
  const sortField = sp.get('sort') ?? 'createdAt';
  const sortDir = sp.get('dir') ?? 'desc';

  useEffect(() => {
    setLoading(true);
    void (async () => {
      try {
        const params = new URLSearchParams(sp);
        if (!params.get('page')) params.set('page', '1');
        if (!params.get('limit')) params.set('limit', '20');
        const response = await api.get<ProductListResponse>(`/admin/products?${params.toString()}`);
        setProducts(response.data.items);
        setTotal(response.data.total);
        setTotalPages(response.data.totalPages);
      } finally {
        setLoading(false);
      }
    })();
  }, [sp, refreshKey]);

  // Keep the input in sync when the URL changes elsewhere (back/forward nav).
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  // Debounce typing into the URL so we don't fetch on every keystroke.
  const debounceReady = useRef(false);
  useEffect(() => {
    if (!debounceReady.current) {
      debounceReady.current = true;
      return;
    }
    if (searchInput === searchQuery) return;
    const t = setTimeout(() => {
      setSp((prev) => {
        const next = new URLSearchParams(prev);
        if (searchInput) next.set('search', searchInput);
        else next.delete('search');
        next.set('page', '1');
        return next;
      });
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, searchQuery, setSp]);

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/admin/products/${pendingDelete.id}`);
      toast.success('Produto eliminado.');
      setPendingDelete(null);
      // setSp with an unchanged URL is a no-op (React Router memoises
      // searchParams on location.search), so refetch via a separate trigger.
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(extractError(err, 'Falha ao eliminar o produto'));
    }
  };

  const setStatus = (value: string): void => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'all') next.delete('status');
      else next.set('status', value);
      next.set('page', '1');
      return next;
    });
  };

  const toggleSort = (field: string): void => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      const curField = prev.get('sort') ?? 'createdAt';
      const curDir = prev.get('dir') ?? 'desc';
      if (curField === field) {
        next.set('dir', curDir === 'asc' ? 'desc' : 'asc');
      } else {
        next.set('sort', field);
        next.set('dir', 'asc');
      }
      next.set('page', '1');
      return next;
    });
  };

  const toggleActive = async (p: Product): Promise<void> => {
    setTogglingId(p.id);
    try {
      await api.put(`/admin/products/${p.id}`, { isActive: !p.isActive });
      setProducts((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, isActive: !x.isActive } : x))
      );
      toast.success(p.isActive ? 'Produto fora de venda.' : 'Produto à venda.');
    } catch (err) {
      toast.error(extractError(err, 'Falha ao atualizar o estado'));
    } finally {
      setTogglingId(null);
    }
  };

  const copyShareLink = async (p: Product): Promise<void> => {
    try {
      await navigator.clipboard.writeText(`${seoDefaults.siteUrl}/produtos/${p.slug}`);
      toast.success('Link da loja copiado.');
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  const duplicate = async (p: Product): Promise<void> => {
    if (duplicatingId) return;
    setDuplicatingId(p.id);
    try {
      const { data } = await api.post<{ id: string; slug: string }>(
        `/admin/products/${p.id}/duplicate`
      );
      toast.success('Produto duplicado.');
      navigate(`/admin/produtos/${data.id}`);
    } catch (err) {
      toast.error(extractError(err, 'Falha ao duplicar o produto'));
      setDuplicatingId(null);
    }
  };

  const sortIndicator = (field: string): JSX.Element => {
    if (sortField !== field)
      return <ChevronsUpDown aria-hidden className="h-3.5 w-3.5 opacity-40" />;
    return sortDir === 'asc' ? (
      <ChevronUp aria-hidden className="h-3.5 w-3.5" />
    ) : (
      <ChevronDown aria-hidden className="h-3.5 w-3.5" />
    );
  };

  const ariaSort = (field: string): 'ascending' | 'descending' | 'none' =>
    sortField === field ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';

  return (
    <>
      <SEO title="Produtos (admin)" noindex />
      <div className="flex items-center justify-between">
        <h1>Produtos ({total})</h1>
        <Link to="/admin/produtos/novo" className={primaryBtn}>
          + Novo produto
        </Link>
      </div>

      <div className="my-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          placeholder="Pesquisar por nome ou SKU…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className={`${inputClass} w-full max-w-sm`}
        />
        <div
          role="group"
          aria-label="Filtrar por estado"
          className="flex items-center gap-1 rounded-md border border-admin-border bg-white p-1 text-sm"
        >
          {[
            { value: 'all', label: 'Todos' },
            { value: 'active', label: 'À venda' },
            { value: 'inactive', label: 'Fora de venda' },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setStatus(opt.value)}
              aria-pressed={statusFilter === opt.value}
              className={`rounded px-3 py-1 transition-colors ${
                statusFilter === opt.value
                  ? 'bg-admin-primary text-white'
                  : 'text-admin-text-soft hover:bg-admin-surface-alt'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-admin-text-soft">A carregar…</p>
      ) : (
        <>
          <table className="overflow-hidden rounded-lg bg-admin-surface shadow-sm">
            <thead>
              <tr className="border-b border-admin-border">
                <th className={`${thClass} w-16`}></th>
                <th className={thClass} aria-sort={ariaSort('name')}>
                  <button
                    type="button"
                    onClick={() => toggleSort('name')}
                    className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-admin-text"
                  >
                    Nome {sortIndicator('name')}
                  </button>
                </th>
                <th className={thClass}>SKU</th>
                <th className={thRight} aria-sort={ariaSort('price')}>
                  <button
                    type="button"
                    onClick={() => toggleSort('price')}
                    className="ml-auto inline-flex items-center gap-1 uppercase tracking-wider hover:text-admin-text"
                  >
                    Preço {sortIndicator('price')}
                  </button>
                </th>
                <th className={thClass} aria-sort={ariaSort('status')}>
                  <button
                    type="button"
                    onClick={() => toggleSort('status')}
                    className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-admin-text"
                  >
                    Estado {sortIndicator('status')}
                  </button>
                </th>
                <th className={thClass}></th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-admin-text-soft">
                    {searchQuery || statusFilter !== 'all'
                      ? 'Nenhum produto corresponde aos filtros.'
                      : 'Ainda não tens produtos. Cria o primeiro com “+ Novo produto”.'}
                  </td>
                </tr>
              ) : null}
              {products.map((p) => {
                const thumb = p.images.find((img) => img.isPrimary) ?? p.images[0];
                return (
                <tr
                  key={p.id}
                  className="border-b border-admin-border last:border-b-0 hover:bg-admin-surface-alt"
                >
                  <td className="p-3">
                    {thumb ? (
                      <img
                        src={thumb.thumbUrl}
                        alt={thumb.altText ?? p.name}
                        className="h-10 w-10 rounded-md object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-md border border-dashed border-admin-border bg-admin-surface-alt text-admin-muted"
                        title="Sem imagem"
                        aria-label="Sem imagem"
                      >
                        <ImageOff aria-hidden className="h-4 w-4" />
                      </div>
                    )}
                  </td>
                  <td className="p-3 font-medium">
                    <Link
                      to={`/admin/produtos/${p.id}`}
                      className="text-admin-text hover:text-admin-primary hover:underline"
                    >
                      {p.name}
                    </Link>
                  </td>
                  <td className="p-3 text-admin-text-soft">{p.sku ?? '—'}</td>
                  <td className="p-3 text-right">{formatEur(p.price)}</td>
                  <td className="p-3">
                    <button
                      type="button"
                      onClick={() => void toggleActive(p)}
                      disabled={togglingId === p.id}
                      aria-label={
                        p.isActive ? `Tirar ${p.name} de venda` : `Pôr ${p.name} à venda`
                      }
                      title={p.isActive ? 'Clica para tirar de venda' : 'Clica para pôr à venda'}
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                        p.isActive
                          ? 'bg-admin-cream text-admin-primary hover:brightness-95'
                          : 'bg-admin-surface-alt text-admin-muted hover:bg-admin-border'
                      }`}
                    >
                      {togglingId === p.id ? '…' : p.isActive ? 'À venda' : 'Fora de venda'}
                    </button>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/admin/produtos/${p.id}`}
                        className={editBtn}
                        aria-label={`Editar ${p.name}`}
                      >
                        <Pencil aria-hidden className="h-3.5 w-3.5" />
                        <span>Editar</span>
                      </Link>
                      <ActionMenu
                        label={`Mais ações para ${p.name}`}
                        items={[
                          {
                            label: 'Copiar link da loja',
                            icon: Link2,
                            onClick: () => void copyShareLink(p),
                          },
                          {
                            label: duplicatingId === p.id ? 'A duplicar…' : 'Duplicar',
                            icon: CopyPlus,
                            disabled: duplicatingId === p.id,
                            onClick: () => void duplicate(p),
                          },
                          {
                            label: p.isActive ? 'Tirar de venda' : 'Pôr à venda',
                            icon: Power,
                            disabled: togglingId === p.id,
                            onClick: () => void toggleActive(p),
                          },
                          {
                            label: 'Eliminar',
                            icon: Trash2,
                            danger: true,
                            onClick: () => setPendingDelete(p),
                          },
                        ]}
                      />
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {totalPages > 1 ? (
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(sp);
                  const page = Math.max(1, Number(sp.get('page') ?? '1') - 1);
                  next.set('page', String(page));
                  setSp(next);
                }}
                className={secondaryBtn}
              >
                Anterior
              </button>
              <span className="text-sm text-admin-text-soft">
                Página {sp.get('page') ?? '1'} de {totalPages}
              </span>
              <button
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(sp);
                  const page = Number(sp.get('page') ?? '1') + 1;
                  next.set('page', String(page));
                  setSp(next);
                }}
                className={secondaryBtn}
              >
                Seguinte
              </button>
            </div>
          ) : null}
        </>
      )}

      {pendingDelete ? (
        <ConfirmDialog
          danger
          title="Eliminar produto"
          message={
            <>
              Tens a certeza que queres eliminar{' '}
              <strong className="font-semibold text-admin-text">
                {pendingDelete.name}
              </strong>
              ? Esta ação não pode ser anulada.
            </>
          }
          confirmLabel="Eliminar"
          busyLabel="A eliminar…"
          onConfirm={confirmDelete}
          onClose={() => setPendingDelete(null)}
        />
      ) : null}
    </>
  );
}
